import DocViewer, { MSDocRenderer } from "@cyntler/react-doc-viewer";
import debounce from "lodash/debounce";
import { useState, useEffect, useMemo, useCallback, useContext, useRef } from "react";
import { Button, Card, CardBody, Input, Label, Spinner } from "reactstrap";
import { generateDocumentPreview, generateSheetPreview, getPreSignedUrl, generateDocument, generateSheet } from "../../exportTemplates/quote/data/quoteTemplateService";
import { useParams } from "react-router-dom";
import { downloadFile } from "../../../utils/fileUtils";
import { useTheme } from "../../../hooks/useTheme";
import { QuoteDataContext } from "../contexts/quoteDataContext";
import { exportQuoteFile } from "../../sharepoint/data/sharepointService";
import { fetchExportCustomFields } from "../../adminExports/data/customFieldsService";
import "../../adminExports/components/renderer.scss";

const AUTO_PREVIEW_STORAGE_KEY = "quoteExportPreview.autoPreview";
const TYPE_DATE = "Date";

const formatIsoDateToDmy = (value) => {
    const raw = String(value ?? "").trim();
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return raw;

    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
};

const getFriendlyErrorMessage = (err, action = "preview") => {
    const raw = String(err?.message || "").toLowerCase();

    if (raw.includes("atob") || raw.includes("not correctly encoded") || raw.includes("invalidcharactererror")) {
        return "The export file could not be retrieved. Please try again, or contact support if it continues.";
    }

    if (raw.includes("network") || raw.includes("failed to fetch")) {
        return "Network error while contacting the export service. Please check your connection and try again.";
    }

    if (raw.includes("no file returned")) {
        return "No file was returned by the export service. Please try again.";
    }

    if (raw.includes("no presigned url")) {
        return "Preview generation finished, but the preview file could not be retrieved. Please try again.";
    }

    return action === "download"
        ? "We could not download the file right now. Please try again."
        : "We could not load the preview right now. Please try again.";
};

const normalizeTemplateConfigJson = (configJson) => {
    if (configJson === null || configJson === undefined || configJson === "") {
        return {};
    }

    if (typeof configJson === "string") {
        try {
            return JSON.parse(configJson);
        } catch {
            return {};
        }
    }

    if (typeof configJson === "object") {
        return configJson;
    }

    return {};
};

const resolveGeneratedFileBlob = (generated, mimeType) => {
    if (!generated) return null;

    if (generated instanceof Blob) {
        return generated;
    }

    if (generated?.file instanceof Blob) {
        return generated.file;
    }

    if (generated instanceof ArrayBuffer) {
        return new Blob([generated], { type: mimeType });
    }

    if (generated?.file instanceof ArrayBuffer) {
        return new Blob([generated.file], { type: mimeType });
    }

    const base64 = typeof generated === "string"
        ? generated
        : (typeof generated?.file === "string" ? generated.file : null);

    if (base64) {
        const byteChars = atob(base64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
        return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
    }

    return null;
};

const stableSerialize = (value) => {
    if (value === null || value === undefined) {
        return JSON.stringify(value);
    }

    if (Array.isArray(value)) {
        return `[${value.map((item) => stableSerialize(item)).join(",")}]`;
    }

    if (typeof value === "object") {
        const keys = Object.keys(value).sort();
        const body = keys
            .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
            .join(",");
        return `{${body}}`;
    }

    return JSON.stringify(value);
};

const QuoteExportPreview = ({
    quoteItems,
    groupedQuoteItems,
    salesForceData,
    customContent,
    template,
    allowDownload = true,
    revisionNumber = null,
    mode = "quote",
    // Overrides for contexts with no quote route (e.g. admin template testers) — falls back to
    // the route params so existing quote-page callers are unaffected.
    quoteId: quoteIdOverride = null,
    quoteRevisionId: quoteRevisionIdOverride = null,
}) => {
    const routeParams = useParams();
    const quoteId = quoteIdOverride ?? routeParams.quoteId;
    const quoteRevisionId = quoteRevisionIdOverride ?? routeParams.quoteRevisionId;
    const theme = useTheme();
    const { itemLookupTypes, suppliers } = useContext(QuoteDataContext) || {};
    const [url, setUrl] = useState("");
    const [status, setStatus] = useState("idle"); // idle | queuing | generating | fetching | ready | error
    const [errorMsg, setErrorMsg] = useState("");
    const [autoPreview, setAutoPreview] = useState(() => {
        try {
            const saved = localStorage.getItem(AUTO_PREVIEW_STORAGE_KEY);
            return saved === null ? true : saved === "true";
        } catch {
            return true;
        }
    });
    const [isDownloading, setIsDownloading] = useState(false);
    const [needsManualPreview, setNeedsManualPreview] = useState(false);
    const [lastPreviewSignature, setLastPreviewSignature] = useState("");
    const [globalCustomFields, setGlobalCustomFields] = useState([]);
    const previousTemplateKeyRef = useRef("");
    const lastAutoRequestedSignatureRef = useRef("");
    // Keep mutable control state in refs so queued/debounced callbacks always read latest values.
    const runPreviewRef = useRef(null);
    const latestQueuedPayloadRef = useRef(null);
    const queueSequenceRef = useRef(0);
    const statusRef = useRef("idle");

    useEffect(() => {
        try {
            localStorage.setItem(AUTO_PREVIEW_STORAGE_KEY, String(autoPreview));
        } catch {
            // Ignore storage write failures and keep runtime behavior intact.
        }
    }, [autoPreview]);

    useEffect(() => {
        const templateKey = template?.id ? `${template?.type || "template"}:${template.id}` : "";
        const hasTemplateChanged = previousTemplateKeyRef.current && previousTemplateKeyRef.current !== templateKey;

        if (hasTemplateChanged && !autoPreview) {
            // In manual mode, switching templates should invalidate and hide the old preview.
            setUrl("");
            setStatus("idle");
            setErrorMsg("");
            setNeedsManualPreview(true);
        }

        previousTemplateKeyRef.current = templateKey;
    }, [template?.id, template?.type, autoPreview]);

    useEffect(() => {
        let isMounted = true;

        const loadGlobalCustomFields = async () => {
            const data = await fetchExportCustomFields();
            if (!isMounted) return;
            setGlobalCustomFields(Array.isArray(data) ? data : []);
        };

        loadGlobalCustomFields();

        return () => {
            isMounted = false;
        };
    }, []);

    const isSheetTemplate = template?.type === "sheet";
    const isPreviewMode = mode === "preview";
    const effectivePreviewKey = isPreviewMode ? "preview" : (quoteRevisionId ?? "preview");
    const resolvedQuoteRevisionId = useMemo(() => {
        const candidate = quoteRevisionId ?? revisionNumber ?? template?.id ?? 0;
        const parsed = Number(candidate);
        return Number.isFinite(parsed) ? parsed : 0;
    }, [quoteRevisionId, revisionNumber, template?.id]);
    const fileName = `${template?.name ?? "preview"} ${effectivePreviewKey}.${isSheetTemplate ? "xlsx" : "docx"}`;

    const dateFieldKeysForTemplate = useMemo(() => {
        const linksRaw = template?.customFields;
        if (!linksRaw) return new Set();

        let links = [];
        try {
            links = typeof linksRaw === "string" ? JSON.parse(linksRaw) : linksRaw;
        } catch {
            links = [];
        }

        if (!Array.isArray(links)) return new Set();

        const globalById = new Map(
            (Array.isArray(globalCustomFields) ? globalCustomFields : [])
                .filter((item) => item?.id !== undefined && item?.id !== null)
                .map((item) => [String(item.id), item])
        );

        const dateKeys = new Set();
        links.forEach((link) => {
            const globalField = globalById.get(String(link?.customFieldId));
            if (!globalField || globalField?.type !== TYPE_DATE) return;

            const fieldKey = String(globalField?.field || "").trim();
            if (fieldKey) dateKeys.add(fieldKey);
        });

        return dateKeys;
    }, [template?.customFields, globalCustomFields]);

    const documentPreviewCustomContent = useMemo(() => {
        const source = Array.isArray(customContent) ? customContent : [];
        if (!source.length || dateFieldKeysForTemplate.size === 0) return source;

        return source.map((item) => {
            const fieldKey = String(item?.field || "").trim();
            if (!fieldKey || !dateFieldKeysForTemplate.has(fieldKey)) return item;

            return {
                ...item,
                value: formatIsoDateToDmy(item?.value),
            };
        });
    }, [customContent, dateFieldKeysForTemplate]);

    const buildDocRequestBody = useCallback(() => ({
        quoteRevisionId: resolvedQuoteRevisionId,
        quoteItems: groupedQuoteItems,
        salesForceData,
        customContent: documentPreviewCustomContent,
        template,
        fileName,
    }), [resolvedQuoteRevisionId, groupedQuoteItems, salesForceData, documentPreviewCustomContent, template, fileName]);

    const buildSheetRequestBody = useCallback(() => ({
        quoteJson: (Array.isArray(quoteItems) ? quoteItems : []).map(item => {
            const itemLookupType = (itemLookupTypes ?? []).find(ilt => ilt.id === item.itemLookupTypeId);
            const supplier = (suppliers ?? []).find(s => s.id === item.supplierId);

            const typeCustomAttributes = (item.typeCustomAttributes || []).reduce((acc, attr) => {
                acc[attr.label] = attr.value;
                return acc;
            }, {});

            return {
                ...item,
                itemLookupTypeName: itemLookupType?.itemLookupTypeName || null,
                supplierName: supplier?.supplierName || null,
                ...typeCustomAttributes,
            };
        }),
        templateId: template?.id,
        configJson: normalizeTemplateConfigJson(template?.configJson),
        customContent: Array.isArray(customContent) ? customContent : [],
        quoteId,
        revisionNumber: Number(revisionNumber ?? 0),
        fileName,
    }), [quoteItems, itemLookupTypes, suppliers, template, customContent, quoteId, revisionNumber, fileName]);

    const buildActiveRequestBody = useCallback(
        () => (isSheetTemplate ? buildSheetRequestBody() : buildDocRequestBody()),
        [isSheetTemplate, buildSheetRequestBody, buildDocRequestBody]
    );

    const buildPreviewSignature = (payload) => {
        try {
            // Use stable key ordering so equivalent payloads do not look different between renders.
            return stableSerialize(payload ?? {});
        } catch {
            return "";
        }
    };

    const runPreview = useCallback(async (payload, signal) => {
        try {
            setStatus("generating");

            const isSheet = isSheetTemplate;

            if (isSheet) {
                await generateSheetPreview(payload);
            } else {
                await generateDocumentPreview(payload);
            }
            if (signal?.aborted) return;

            setStatus("fetching");

            const type = isSheet ? "sheet" : "document";
            const documentUrl = await getPreSignedUrl(`${type}preview/${fileName}`);
            if (signal?.aborted) return;

            if (documentUrl?.presignedUrl) {
                setUrl(documentUrl.presignedUrl);
                setStatus("ready");
                setLastPreviewSignature(buildPreviewSignature(payload));
                setNeedsManualPreview(false);
            } else {
                throw new Error("No presigned URL returned");
            }
        } catch (err) {
            if (!signal?.aborted) {
                console.error("Preview generation failed:", err);
                setErrorMsg(getFriendlyErrorMessage(err, "preview"));
                setStatus("error");
            }
        }
    }, [fileName, isSheetTemplate]);

    useEffect(() => {
        runPreviewRef.current = runPreview;
    }, [runPreview]);

    useEffect(() => {
        statusRef.current = status;
    }, [status]);

    const handlePreviewClick = useCallback(async () => {
        const payload = buildActiveRequestBody();
        setStatus("queuing");
        setErrorMsg("");
        setUrl("");
        await runPreview(payload);
    }, [buildActiveRequestBody, runPreview]);

    const tryUploadExportToSharePoint = useCallback(async (blob, exportName) => {
        const sharepointFolder = salesForceData?.opportunity?.SharePoint_Folder__c;
        const revisionForUpload = revisionNumber ?? Number(quoteRevisionId);

        if (!sharepointFolder || !quoteId || !revisionForUpload) {
            return { uploaded: false, reason: "missing-sharepoint-context" };
        }

        const file = new File([blob], exportName, {
            type: blob.type || "application/octet-stream",
            lastModified: Date.now(),
        });

        const form = new FormData();
        form.append("file", file);
        form.append("sharepoint", sharepointFolder);
        form.append("quoteId", quoteId);
        form.append("revisionNumber", revisionForUpload);

        const uploadResult = await exportQuoteFile(form);
        if (uploadResult?.responseOk) {
            if (uploadResult?.message) {
                window.open(uploadResult.message, "_blank", "noopener,noreferrer");
            }
            return { uploaded: true };
        }

        return { uploaded: false, reason: "upload-failed" };
    }, [quoteId, quoteRevisionId, revisionNumber, salesForceData?.opportunity?.SharePoint_Folder__c]);

    const handleDownloadClick = useCallback(async () => {
        try {
            const payload = buildActiveRequestBody();
            const isSheet = isSheetTemplate;

            setIsDownloading(true);
            setErrorMsg("");

            const generated = isSheet
                ? await generateSheet(payload)
                : await generateDocument(payload);

            const mimeType = isSheet
                ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

            const downloadName = generated?.fileName || payload.fileName;

            const blob = resolveGeneratedFileBlob(generated, mimeType);
            if (!blob) {
                throw new Error("No file returned from export generation");
            }

            const uploadResult = await tryUploadExportToSharePoint(blob, downloadName);
            if (!uploadResult.uploaded) {
                await downloadFile(blob, downloadName);
            }

            // Download succeeded; clear any stale error state from earlier failures.
            setErrorMsg("");

            // In manual mode, refresh preview immediately so it matches the downloaded file.
            if (!autoPreview) {
                setStatus("queuing");
                setUrl("");
                await runPreview(payload);
            }
        } catch (err) {
            console.error("Export download failed:", err);
            setErrorMsg(getFriendlyErrorMessage(err, "download"));
            setStatus("error");
        } finally {
            setIsDownloading(false);
        }
    }, [autoPreview, buildActiveRequestBody, isSheetTemplate, runPreview, tryUploadExportToSharePoint]);

    // Debounce the whole flow so rapid prop changes collapse into a single run
    const debouncedRun = useMemo(
        () =>
            debounce(
                (payload) => runPreviewRef.current?.(payload),
                3000,
                { leading: false, trailing: true, maxWait: 10000 }
            ),
        // Keep one debounced instance for the component lifetime to avoid dropping queued runs.
        []
    );

    useEffect(() => () => debouncedRun.cancel(), [debouncedRun]);

    useEffect(() => {
        if (status !== "queuing") return;

        // Safety net: if queued work never transitions to generating, force a single run.
        const queuedSequence = queueSequenceRef.current;
        const timeoutId = setTimeout(() => {
            const isSameQueue = queuedSequence === queueSequenceRef.current;
            const isStillQueuing = statusRef.current === "queuing";
            const payload = latestQueuedPayloadRef.current;

            if (isSameQueue && isStillQueuing && payload) {
                debouncedRun.cancel();
                runPreviewRef.current?.(payload);
            }
        }, 12000);

        return () => clearTimeout(timeoutId);
    }, [status, debouncedRun]);

    useEffect(() => {
        if (autoPreview) {
            setNeedsManualPreview(false);
        }
    }, [autoPreview]);

    useEffect(() => {
        if (!template) {
            // No template means preview cannot run; clear queued work and reset state.
            debouncedRun.cancel();
            setStatus("idle");
            setNeedsManualPreview(false);
            lastAutoRequestedSignatureRef.current = "";
            return;
        }

        if (isSheetTemplate && (!quoteItems || quoteItems.length === 0 || !quoteRevisionId)) {
            // Cancel stale queue when sheet prerequisites are incomplete.
            debouncedRun.cancel();
            if (autoPreview) {
                setStatus("idle");
                setNeedsManualPreview(false);
            }
            return;
        }

        if (!isSheetTemplate && (!template?.customFields || !customContent || !salesForceData)) {
            // Keep status honest when doc preview inputs are not ready yet.
            debouncedRun.cancel();
            if (autoPreview) {
                setStatus("waiting for custom content");
                setNeedsManualPreview(false);
            } else {
                setStatus("idle");
            }
            return;
        }

        if (isSheetTemplate) {
            const requestBody = buildActiveRequestBody();
            const currentSignature = buildPreviewSignature(requestBody);

            if (autoPreview) {
                const isPreviewStale = !!lastPreviewSignature && currentSignature !== lastPreviewSignature;
                const shouldGeneratePreview = currentSignature !== lastAutoRequestedSignatureRef.current;
                setNeedsManualPreview(isPreviewStale);
                if (shouldGeneratePreview) {
                    lastAutoRequestedSignatureRef.current = currentSignature;
                    latestQueuedPayloadRef.current = requestBody;
                    queueSequenceRef.current += 1;
                    setStatus("queuing");
                    setErrorMsg("");
                    setUrl("");
                    debouncedRun(requestBody);
                }
            } else {
                setNeedsManualPreview(!!lastPreviewSignature && currentSignature !== lastPreviewSignature);
            }

            return;
        }

        const rawCustomFields = template.customFields;
        let jsonCustomFields = rawCustomFields;
        if (typeof rawCustomFields === "string") {
            try {
                jsonCustomFields = JSON.parse(rawCustomFields);
            } catch {
                jsonCustomFields = [];
            }
        }

        const requiredSectionGuids = [...new Set(
            (Array.isArray(jsonCustomFields) ? jsonCustomFields : [])
                .map((item) => String(item?.sectionGuid || "").trim())
                .filter(Boolean)
        )];
        const customContentSectionGuids = new Set(
            (Array.isArray(customContent) ? customContent : [])
                .map((item) => String(item?.sectionGuid || "").trim())
                .filter(Boolean)
        );

        const isMissingRequiredSectionContent =
            requiredSectionGuids.length > 0 &&
            !requiredSectionGuids.every((guid) => customContentSectionGuids.has(guid));

        if (isMissingRequiredSectionContent && autoPreview) {
            // In auto-preview mode, wait until required custom section content is present.
            setUrl("");
            setStatus("waiting for custom content");
            setNeedsManualPreview(false);
            return;
        }

        if (isMissingRequiredSectionContent && !autoPreview) {
            // Manual mode should still allow explicit preview attempts.
            setStatus("idle");
        }

        const requestBody = buildActiveRequestBody();
        const currentSignature = buildPreviewSignature(requestBody);

        if (autoPreview) {
            const isPreviewStale = !!lastPreviewSignature && currentSignature !== lastPreviewSignature;
            const shouldGeneratePreview = currentSignature !== lastAutoRequestedSignatureRef.current;
            setNeedsManualPreview(isPreviewStale);
            if (shouldGeneratePreview) {
                lastAutoRequestedSignatureRef.current = currentSignature;
                latestQueuedPayloadRef.current = requestBody;
                queueSequenceRef.current += 1;
                setStatus("queuing");
                setErrorMsg("");
                setUrl(""); // clear any previous preview while new one is queued
                debouncedRun(requestBody);
            }
        } else {
            setNeedsManualPreview(!!lastPreviewSignature && currentSignature !== lastPreviewSignature);
        }

        return;
    }, [
        quoteItems,
        groupedQuoteItems,
        salesForceData,
        customContent,
        template,
        quoteRevisionId,
        debouncedRun,
        fileName,
        autoPreview,
        buildActiveRequestBody,
        lastPreviewSignature,
        isSheetTemplate,
    ]);

    const docs =
        url
            ? [{
                uri: url,
                fileType: template?.s3Key?.split(".").pop()?.toLowerCase() || (isSheetTemplate ? "xlsx" : "docx"),
                name: template?.s3Key || fileName,
            }]
            : [];

    const isLoadingStatus = ["queuing", "generating", "fetching"].includes(status);
    const hasFreshPreview = status === "ready" && !!url && !needsManualPreview;
    const isPreviewOutOfDate = autoPreview ? !hasFreshPreview : needsManualPreview;
    const isUpdatingPreview = autoPreview && isLoadingStatus;
    const controlsPanelBackground = theme === "dark-mode" ? "var(--bg-color-contrast1)" : "#E4E4E4";
    const controlsPanelBorder = "var(--border-color)";
    const controlsLabelColor = "var(--text-color)";

    const Loading = ({ label }) => (
        <div className="h-100 d-flex align-items-center justify-content-center flex-column text-center">
            <div className="spinner-border" role="status" aria-hidden="true" />
            <p className="mt-3 mb-0">{label}</p>
        </div>
    );

    const ErrorState = ({ message }) => (
        <div className="h-100 d-flex align-items-center justify-content-center flex-column text-center text-danger">
            <i className="bi bi-exclamation-triangle fs-1"></i>
            <p className="mt-2 mb-0">{message || "Something went wrong loading the preview."}</p>
        </div>
    );

    const InfoState = ({ message }) => (
        <div className="h-100 d-flex align-items-center justify-content-center flex-column text-center">
            <i className="bi bi-info-circle fs-1"></i>
            <p className="mt-2 mb-0">{message}</p>
        </div>
    );

    return (
        <Card style={{ width: "100%", height: "100%", minHeight: 0 }}>
            <CardBody className="h-100 d-flex flex-column" style={{ minHeight: 0 }}>
                {!template ? (
                    <div className="h-100 d-flex align-items-center justify-content-center flex-column text-center">
                        <i className="bi bi-file-earmark-text fs-1"></i>
                        <p className="mt-2 mb-0">Please select a template.</p>
                    </div>
                ) : (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-shrink-0">
                            <div />
                            <div
                                className="d-flex justify-content-end align-items-center gap-3"
                            >
                                <span
                                    className={
                                        isUpdatingPreview || isPreviewOutOfDate
                                            ? "text-warning small fw-semibold d-inline-flex align-items-center gap-1"
                                            : "text-success small fw-semibold d-inline-flex align-items-center gap-1"
                                    }
                                >
                                    {isUpdatingPreview ? (
                                        <>
                                            <Spinner size="sm" />
                                            <>Updating preview...</>
                                        </>
                                    ) : isPreviewOutOfDate ? (
                                        <>
                                            <i className="bi bi-exclamation-triangle" />
                                            <>Preview out of date</>
                                        </>
                                    ) : (
                                        <>
                                            <>Up to date</>
                                            <i className="bi bi-check-circle" />
                                        </>
                                    )}
                                </span>
                                {!autoPreview && (
                                    <Button color="primary" onClick={handlePreviewClick} disabled={!template}>
                                        Preview
                                    </Button>
                                )}
                                <div
                                    className="d-flex align-items-center gap-2"
                                    style={{
                                        border: "1px solid var(--border-color2)",
                                        borderRadius: "0.375rem",
                                        padding: "0.25rem 0.5rem",
                                        backgroundColor: theme === "dark-mode" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)"
                                    }}
                                >
                                    <Input
                                        id="auto-preview-checkbox"
                                        type="checkbox"
                                        checked={autoPreview}
                                        onChange={(e) => setAutoPreview(e.target.checked)}
                                        style={{
                                            accentColor: "#B2D953",
                                            backgroundColor: theme === "dark-mode" ? "#22333b" : "#FFFFFF",
                                            width: "1.05rem",
                                            height: "1.05rem",
                                            border: `2px solid ${theme === "dark-mode" ? "#9fb3bf" : "#4f5d66"}`,
                                            borderColor: theme === "dark-mode" ? "#9fb3bf" : "#4f5d66",
                                            boxShadow: theme === "dark-mode"
                                                ? "0 0 0 1px rgba(16, 24, 28, 0.85)"
                                                : "0 0 0 1px rgba(79, 93, 102, 0.15)"
                                        }}
                                    />
                                    <Label className="mb-0" style={{ color: controlsLabelColor }} for="auto-preview-checkbox">Auto-preview</Label>
                                </div>
                                {allowDownload && (
                                    <Button color="secondary" onClick={handleDownloadClick} disabled={!template || isDownloading}>
                                        {isDownloading ? <Spinner size="sm" /> : "Export"}
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div className="d-flex flex-column flex-grow-1" style={{ position: "relative", minHeight: 0, overflow: "hidden" }}>
                            {status === "error" ? (
                                <ErrorState message={errorMsg} />
                            ) : docs.length > 0 && status === "ready" ? (
                                <div className="doc-wrap flex-grow-1" style={{ width: "100%", minHeight: 0, overflow: "hidden" }}>
                                    <div className="h-100" style={{ width: "100%", overflow: "hidden" }}>
                                        <DocViewer
                                            pluginRenderers={[MSDocRenderer]}
                                            documents={docs}
                                            style={{ width: "100%", height: "100%" }}
                                        />    
                                        { mode === "quote" && 
                                            <div className="doc-footer-mask" />
                                        }                                    
                                    </div>
                                </div>
                            ) : isLoadingStatus ? (<>
                                <Loading
                                    label={
                                        status === "queuing"
                                            ? "Preparing to generate preview..."
                                            : status === "generating"
                                                ? "Generating preview..."
                                                : status === "fetching"
                                                    ? "Fetching document..."
                                                    : "Loading..."
                                    }
                                />
                            </>
                            ) : (
                                <InfoState
                                    message={
                                        !autoPreview
                                            ? "Auto-preview is off. Click Preview to generate the document preview."
                                            : status === "waiting for custom content"
                                                ? "Waiting for required custom content before preview can be generated."
                                                : "Preview will appear here when ready."
                                    }
                                />
                            )}
                        </div>
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default QuoteExportPreview;
